import { jsxs, jsx } from "react/jsx-runtime";
import { css, cva } from "@/styled-system/css";
import { Box, Text } from "@zillow/constellation-10";
import React from "react";
import { ALL_CATEGORY, normalizeFilteredFaqCategories } from "./constants.esm.js";
const ActiveCategoryContext = React.createContext(ALL_CATEGORY);
const rootStyles = css.raw({
  width: "100%"
});
const filterBarStyles = css.raw({
  display: "flex",
  flexWrap: "wrap",
  gap: "tighter",
  marginBottom: "default"
});
const filterChipRecipe = cva({
  base: {
    appearance: "none",
    borderWidth: "1px",
    borderStyle: "solid",
    // Pill shape. `radii` has no `full`; `capsule` (999px) is the token the
    // design system uses for fully rounded objects. Bare token name, not
    // `radii.capsule` — the borderRadius utility already scopes to `radii`.
    borderRadius: "capsule",
    minHeight: "40px",
    paddingBlock: "tighter",
    paddingInline: "default",
    // One composition token instead of hardcoded fontSize/lineHeight plus a
    // nonexistent `fontWeights.medium`. This is what the design system's own
    // chip uses (assist-chip-label.styles.ts), and it resolves to
    // 14px / 24px / weight `emphasis`.
    textStyle: "body-sm-bold",
    cursor: "pointer",
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "0.15s",
    transitionTimingFunction: "ease",
    _focusVisible: {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: "border.accent.brand.hero",
      outlineOffset: "2px"
    }
  },
  variants: {
    selected: {
      true: {
        backgroundColor: "bg.accent.brand.hero",
        borderColor: "bg.accent.brand.hero",
        color: "text.onImpact.neutral",
        _hover: {
          backgroundColor: "bg.accent.brand.hero",
          borderColor: "bg.accent.brand.hero",
          color: "text.onImpact.neutral"
        }
      },
      false: {
        backgroundColor: "bg.neutral",
        borderColor: "border.neutral",
        color: "text.neutral",
        _hover: {
          borderColor: "bg.accent.brand.hero",
          color: "text.accent.brand.hero"
        }
      }
    }
  },
  defaultVariants: {
    selected: false
  }
});
const fallbackStyles = css.raw({
  marginTop: "default",
  padding: "loose",
  textAlign: "center",
  backgroundColor: "bg.neutral",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "border.neutral",
  // Bare token name; `radii.lg` emitted `border-radius: radii.lg`, which
  // browsers drop.
  borderRadius: "lg"
});
const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const buildFilterOptions = (categories) => [{
  id: ALL_CATEGORY,
  label: ALL_CATEGORY
}, ...normalizeFilteredFaqCategories(categories)];
const isGroupVisible = (category, activeCategory) => activeCategory === ALL_CATEGORY || category != null && category === activeCategory;
const countVisibleGroups = (children, activeCategory) => React.Children.toArray(children).filter((child) => {
  if (!React.isValidElement(child)) return false;
  const {
    category
  } = child.props ?? {};
  return isGroupVisible(category, activeCategory);
}).length;
const DefaultFallback = () => /* @__PURE__ */ jsx(Box, { css: fallbackStyles, role: "status", "aria-live": "polite", children: /* @__PURE__ */ jsx(Text, { textStyle: "body", css: {
  color: "text.subtle"
}, children: "Can't find what you're looking for?" }) });
const FilteredFAQFilters = ({
  categories,
  activeCategory,
  onCategoryChange,
  idPrefix
}) => {
  const filterOptions = React.useMemo(() => buildFilterOptions(categories), [categories]);
  const handleKeyDown = (event) => {
    var _a, _b;
    const chips = Array.from(event.currentTarget.querySelectorAll("button"));
    const currentIndex = chips.findIndex((chip) => chip.getAttribute("aria-pressed") === "true");
    if (currentIndex === -1) return;
    let nextIndex = currentIndex;
    if (event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % chips.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + chips.length) % chips.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = chips.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    const nextCategory = (_a = filterOptions[nextIndex]) == null ? void 0 : _a.id;
    if (nextCategory) {
      onCategoryChange(nextCategory);
    }
    (_b = chips[nextIndex]) == null ? void 0 : _b.focus();
  };
  return /* @__PURE__ */ jsx(Box, { css: filterBarStyles, role: "group", "aria-label": "Filter FAQs by category", onKeyDown: handleKeyDown, children: filterOptions.map((option) => {
    const selected = option.id === activeCategory;
    return /* @__PURE__ */ jsx("button", { type: "button", id: `${idPrefix}-filter-${slugify(option.id)}`, "aria-pressed": selected, tabIndex: selected ? 0 : -1, className: filterChipRecipe({
      selected
    }), onClick: () => onCategoryChange(option.id), children: option.label }, option.id);
  }) });
};
const FilteredFAQGroup = ({
  category,
  children,
  style,
  ...props
}) => {
  const activeCategory = React.useContext(ActiveCategoryContext);
  const isVisible = isGroupVisible(category, activeCategory);
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...props,
      hidden: !isVisible,
      style: isVisible ? style : {
        ...style,
        display: "none"
      },
      children
    }
  );
};
FilteredFAQGroup.displayName = "FilteredFAQ.Group";
const FilteredFAQRoot = React.forwardRef(({
  categories = [],
  category,
  defaultCategory = ALL_CATEGORY,
  onCategoryChange,
  showFilters,
  fallback,
  children,
  css: cssProp,
  ...props
}, ref) => {
  const reactId = React.useId();
  const idPrefix = `filtered-faq-${reactId.replace(/[^a-zA-Z0-9]/g, "")}`;
  const listId = `${idPrefix}-list`;
  const shouldShowFilters = showFilters ?? true;
  const [internalCategory, setInternalCategory] = React.useState(defaultCategory);
  const activeCategory = category !== void 0 ? category : internalCategory;
  const handleCategoryChange = React.useCallback((nextCategory) => {
    if (category === void 0) {
      setInternalCategory(nextCategory);
    }
    onCategoryChange == null ? void 0 : onCategoryChange(nextCategory);
  }, [category, onCategoryChange]);
  const visibleCount = React.useMemo(() => countVisibleGroups(children, activeCategory), [children, activeCategory]);
  return (
    // `cssProp` is merged rather than spread with `...props` so a
    // consumer's `css` layers over `width: 100%` instead of replacing it.
    /* @__PURE__ */ jsxs(Box, { ref, ...props, css: css.raw(rootStyles, cssProp), children: [
      shouldShowFilters && /* @__PURE__ */ jsx(FilteredFAQFilters, { categories, activeCategory, onCategoryChange: handleCategoryChange, idPrefix }),
      /* @__PURE__ */ jsx(ActiveCategoryContext.Provider, { value: activeCategory, children: /* @__PURE__ */ jsx(Box, { id: listId, "aria-live": "polite", children }) }),
      visibleCount === 0 && (fallback !== void 0 ? fallback : /* @__PURE__ */ jsx(DefaultFallback, {}))
    ] })
  );
});
FilteredFAQRoot.displayName = "FilteredFAQ";
const FilteredFAQ = Object.assign(FilteredFAQRoot, {
  Group: FilteredFAQGroup
});
export {
  FilteredFAQ as default
};
